import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ContactUs.module.css';
import API_BASE_URL from '../../config/api';

const ContactUs = () => {
  const { t, i18n } = useTranslation();
  const [contact, setContact] = useState(null);
  const [marketingTeam, setMarketingTeam] = useState([]);

  const [formData, setFormData] = useState({ name: '', phone: '', email: '', company: '', message: '' });
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      const res = await fetch(`${API_BASE_URL}/contact-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Server error');
      setFormStatus('success');
      setFormData({ name: '', phone: '', email: '', company: '', message: '' });
    } catch {
      setFormStatus('error');
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchContact = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(
          `${API_BASE_URL}/contact?lang=${lang}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        setContact(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load contact page', error);
        }
      }
    };

    fetchContact();

    return () => controller.abort();
  }, [i18n.language]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchMarketingTeam = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(
          `${API_BASE_URL}/marketing-team?lang=${lang}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        setMarketingTeam(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load marketing team', error);
        }
      }
    };

    fetchMarketingTeam();

    return () => controller.abort();
  }, [i18n.language]);

  return (
    <div className={styles.contactPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{contact?.title || t('contactPage.title')}</h1>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className={styles.contactSection}>
        <div className={styles.container}>
          <div className={styles.contactGrid}>
            {/* Address Card */}
            <div className={styles.contactCard}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{contact?.addressTitle || t('contactPage.address.title')}</h2>
              <div className={styles.addressContent}>
                <p>{contact?.addressLine1 || t('contactPage.address.line1')}</p>
                <p>{contact?.addressLine2 || t('contactPage.address.line2')}</p>
                <p>{contact?.addressLine3 || t('contactPage.address.line3')}</p>
              </div>
            </div>

            {/* Phones Card */}
            <div className={styles.contactCard}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('contactPage.phones.executive.title')}</h2>
              <div className={styles.phonesContent}>
                <div className={styles.phoneItem}>
                  <span className={styles.phoneLabel}>{t('contactPage.phones.executive.title')}:</span>
                  <a href={`tel:${(contact?.execPhone || t('contactPage.phones.executive.number')).replace(/[()-\s]/g, '')}`} className={styles.phoneNumber}>
                    {contact?.execPhone || t('contactPage.phones.executive.number')}
                  </a>
                </div>
                <div className={styles.phoneItem}>
                  <span className={styles.phoneLabel}>{t('contactPage.phones.boardFax.title')}:</span>
                  <span className={styles.phoneNumber}>{contact?.boardFax || t('contactPage.phones.boardFax.number')}</span>
                </div>
                <div className={styles.phoneItem}>
                  <span className={styles.phoneLabel}>{t('contactPage.phones.marketing.title')}:</span>
                  <a href={`tel:${(contact?.marketingPhone || t('contactPage.phones.marketing.number')).replace(/[()-\s]/g, '')}`} className={styles.phoneNumber}>
                    {contact?.marketingPhone || t('contactPage.phones.marketing.number')}
                  </a>
                </div>
                <div className={styles.phoneItem}>
                  <span className={styles.phoneLabel}>{t('contactPage.phones.financial.title')}:</span>
                  <a href={`tel:${(contact?.financialPhone || t('contactPage.phones.financial.number')).replace(/[()-\s]/g, '')}`} className={styles.phoneNumber}>
                    {contact?.financialPhone || t('contactPage.phones.financial.number')}
                  </a>
                </div>
                <div className={styles.phoneItem}>
                  <span className={styles.phoneLabel}>{t('contactPage.phones.quality.title')}:</span>
                  <a href={`tel:${(contact?.qualityPhone || t('contactPage.phones.quality.number')).replace(/[()-\s]/g, '')}`} className={styles.phoneNumber}>
                    {contact?.qualityPhone || t('contactPage.phones.quality.number')}
                  </a>
                </div>
              </div>
            </div>

            {/* Working Hours Card */}
            <div className={styles.contactCard}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>{t('contactPage.workingHours.title')}</h2>
              <div className={styles.hoursContent}>
                <p className={styles.hoursWeekdays}>{contact?.weekdays || t('contactPage.workingHours.weekdays')}</p>
                <p className={styles.hoursTime}>{contact?.time || t('contactPage.workingHours.time')}</p>
                <p className={styles.hoursWeekend}>{contact?.weekend || t('contactPage.workingHours.weekend')}</p>
              </div>
            </div>

            {/* Emails Card */}
            <div className={styles.contactCard}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h2 className={styles.cardTitle}>Email</h2>
              <div className={styles.emailsContent}>
                <a href={`mailto:${contact?.infoEmail || t('contactPage.emails.info')}`} className={styles.emailLink}>
                  <span>{contact?.infoEmail || t('contactPage.emails.info')}</span>
                </a>
                <a href={`mailto:${contact?.salesEmail || t('contactPage.emails.sales')}`} className={styles.emailLink}>
                  <span>{contact?.salesEmail || t('contactPage.emails.sales')}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className={styles.mapSection}>
            <h2 className={styles.mapTitle}>{t('contactPage.map.title')}</h2>
            <div className={styles.mapContainer}>
              <iframe
                src={contact?.mapEmbedUrl || 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1731.6429070999316!2d31.32379!3d29.769376!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14583579457d765f%3A0x841e3873f394414d!2z2LTYsdmD2Kkg2KfZhNmG2LXYsSDZhNi12YbYp9i52Kkg2KfZhNmF2LfYsdmI2YLYp9iq!5e0!3m2!1sen!2sus!4v1767178438953!5m2!1sen!2sus'}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Company Location"
                className={styles.mapIframe}
              ></iframe>
            </div>
          </div>

          {/* Marketing Team Section */}
          {marketingTeam.length > 0 && (
            <div className={styles.marketingTeamSection}>
              <h2 className={styles.marketingTeamTitle}>
                {t('contactPage.marketingTeam.title', 'Marketing Team')}
              </h2>
              <div className={styles.marketingTeamGrid}>
                {marketingTeam.map((member) => (
                  <div key={member.id} className={styles.marketingTeamCard}>
                    <h3 className={styles.memberRole}>{member.title}</h3>
                    <div className={styles.memberPhotoWrap}>
                      {member.image_url ? (
                        <img
                          src={member.image_url}
                          alt={member.name}
                          className={styles.memberPhoto}
                        />
                      ) : (
                        <div className={styles.memberPhotoPlaceholder}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <h4 className={styles.memberName}>{member.name}</h4>
                    {(member.phone || member.email) && (
                      <div className={styles.memberContacts}>
                        {member.phone && (
                          <a href={`tel:${member.phone.replace(/\s/g, '')}`} className={styles.memberContact}>
                            <span className={styles.contactIcon}>
                              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                            </span>
                            {member.phone}
                          </a>
                        )}
                        {member.email && (
                          <a href={`mailto:${member.email}`} className={styles.memberContact}>
                            <span className={styles.contactIcon}>
                              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                              </svg>
                            </span>
                            {member.email}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Contact Form */}
          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>{t('contactPage.form.title')}</h2>
              <div className={styles.formUnderline}></div>
              <p className={styles.formSubtitle}>{t('contactPage.form.subtitle')}</p>
            </div>

            {formStatus === 'success' ? (
              <div className={styles.formSuccess}>
                <div className={styles.successIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h3>{t('contactPage.form.successTitle')}</h3>
                <p>{t('contactPage.form.successText')}</p>
                <button className={styles.sendAnotherBtn} onClick={() => setFormStatus('idle')}>
                  {t('contactPage.form.submit')}
                </button>
              </div>
            ) : (
              <form className={styles.contactForm} onSubmit={handleFormSubmit} noValidate>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('contactPage.form.fullName')} *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder={t('contactPage.form.fullNamePlaceholder')}
                      className={styles.formInput}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('contactPage.form.phone')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder={t('contactPage.form.phonePlaceholder')}
                      className={styles.formInput}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('contactPage.form.email')} *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder={t('contactPage.form.emailPlaceholder')}
                      className={styles.formInput}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{t('contactPage.form.company')}</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleFormChange}
                      placeholder={t('contactPage.form.companyPlaceholder')}
                      className={styles.formInput}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('contactPage.form.message')} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    placeholder={t('contactPage.form.messagePlaceholder')}
                    className={styles.formTextarea}
                    rows={6}
                    required
                  />
                </div>

                {formStatus === 'error' && (
                  <p className={styles.formError}>{t('contactPage.form.errorText')}</p>
                )}

                <button
                  type="submit"
                  className={styles.formSubmitBtn}
                  disabled={formStatus === 'sending'}
                >
                  {formStatus === 'sending' ? t('contactPage.form.sending') : t('contactPage.form.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
